import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  offerSeriesFormSchema,
  type OfferSeriesFormValues,
} from "@/features/offers/form-schema";
import {
  collidingRowIds,
  endTimeAfter,
  expandSeriesPattern,
  noSaleWindowDefaults,
  overlappingRowIds,
  patternRefusal,
  planOffer,
  rowIdsClosingSaleAfterItEnds,
  rowIdsClosingSaleBeforeItOpens,
  rowIdsEndingWhenTheyStart,
  rowIdsWithSaleAlreadyClosed,
  seriesMessage,
  startOfLocalDay,
  submissionRefusal,
  templateRefusal,
  type PlannedOffer,
  type SaleWindowDefaults,
} from "@/domain/offer/series";
import { saleCloseOf } from "@/domain/activity/rules";
import { hasSaleClosed } from "@/domain/offer/rules";
import type { OfferDraft } from "@/features/offers/hooks/useOfferForm";
import type { OfferActivityOption } from "@/data/repos/partnerOffersRepo";
import type { PartnerOffer } from "@/domain/offer/types";
import { addDays, formatDate } from "@/lib/time";

function emptyValues(): OfferSeriesFormValues {
  return {
    activity_id: "",
    weekdays: [],
    start_time: "",
    end_time: "",
    range_from: startOfLocalDay(addDays(new Date(), 1)),
    range_to: startOfLocalDay(addDays(new Date(), 28)),
  };
}

export function rowTime(instant: Date): string {
  return formatDate(instant, "HH:mm");
}

export interface OfferSeriesRowEdit {
  startTime?: string;
  endTime?: string;
}

export function useOfferSeries(
  templates: OfferActivityOption[],
  publishedOffers: PartnerOffer[],
  havePublishedOffersLoaded: boolean,
) {
  const form = useForm<OfferSeriesFormValues>({
    resolver: zodResolver(offerSeriesFormSchema),
    mode: "onBlur",
    defaultValues: emptyValues(),
  });
  const [rows, setRows] = useState<PlannedOffer[] | null>(null);
  const [patternRefused, setPatternRefused] = useState<string | null>(null);

  const selectedTemplateId = form.watch("activity_id");
  const template = useMemo(
    () => templates.find((option) => String(option.id) === selectedTemplateId) ?? null,
    [templates, selectedTemplateId],
  );

  const saleDefaults = useMemo<SaleWindowDefaults>(
    () =>
      template
        ? { leadTimeHours: template.saleLeadTimeHours, close: saleCloseOf(template) }
        : noSaleWindowDefaults,
    [template],
  );

  const [weekdays, startTime, endTime, rangeFrom, rangeTo] = form.watch([
    "weekdays",
    "start_time",
    "end_time",
    "range_from",
    "range_to",
  ]);

  // The end follows the start only while it is still what the šablona's length
  // put there — an end the partner typed is never written over.
  const prefilledEndTime = useRef("");
  const defaultDurationMinutes = template?.defaultDurationMinutes ?? null;
  useEffect(() => {
    if (form.getValues("end_time") !== prefilledEndTime.current) return;
    prefilledEndTime.current = endTimeAfter(startTime, defaultDurationMinutes);
    form.setValue("end_time", prefilledEndTime.current);
  }, [startTime, defaultDurationMinutes, form]);

  // A preview belongs to the pattern it came from, šablona included: publishing
  // rows the form no longer describes would publish hours the partner has since
  // changed on screen. Corrections made inside the preview are not pattern
  // changes and survive.
  const describedPattern = JSON.stringify([
    selectedTemplateId,
    [...weekdays].sort(),
    startTime,
    endTime,
    rangeFrom?.getTime() ?? null,
    rangeTo?.getTime() ?? null,
  ]);
  useEffect(() => {
    setRows(null);
    setPatternRefused(null);
  }, [describedPattern]);

  // A termín that stopped selling no longer occupies its hour — a cancelled
  // Tuesday would otherwise block that slot forever, with no way to override.
  const publishedHours = useMemo(
    () =>
      publishedOffers
        .filter((offer) => String(offer.activityId) === selectedTemplateId)
        .filter((offer) => !hasSaleClosed(offer.saleWindow))
        .map((offer) => ({ startTime: offer.startTime, endTime: offer.endTime })),
    [publishedOffers, selectedTemplateId],
  );

  const templateRefused = template
    ? templateRefusal({
        priceDiscounted: template.defaultPriceDiscounted,
        capacity: template.defaultCapacity,
      })
    : null;

  const canDescribePattern = havePublishedOffersLoaded && templateRefused === null;

  const buildPreview = form.handleSubmit((values) => {
    if (!canDescribePattern) return;
    const pattern = {
      weekdays: values.weekdays,
      startTime: values.start_time,
      endTime: values.end_time,
      from: values.range_from,
      to: values.range_to,
    };
    const refusal = patternRefusal(pattern);
    setPatternRefused(refusal);
    setRows(refusal ? null : expandSeriesPattern(pattern, saleDefaults));
  });

  const correctRow = useCallback(
    (id: string, edit: OfferSeriesRowEdit) => {
      setRows((current) =>
        (current ?? []).map((row) => {
          if (row.id !== id) return row;
          const corrected = planOffer(
            row.id,
            startOfLocalDay(row.startTime),
            edit.startTime ?? rowTime(row.startTime),
            edit.endTime ?? rowTime(row.endTime),
            saleDefaults,
          );
          return corrected ?? row;
        }),
      );
    },
    [saleDefaults],
  );

  const removeRow = useCallback((id: string) => {
    setRows((current) => (current ?? []).filter((row) => row.id !== id));
  }, []);

  const keepUnpublishedRows = useCallback((unpublished: OfferDraft[]) => {
    const unpublishedStarts = new Set(unpublished.map((draft) => draft.startTime.getTime()));
    setRows((current) =>
      (current ?? []).filter((row) => unpublishedStarts.has(row.startTime.getTime())),
    );
  }, []);

  const reset = useCallback(() => {
    prefilledEndTime.current = "";
    form.reset(emptyValues());
    setRows(null);
    setPatternRefused(null);
  }, [form]);

  const refusal = !havePublishedOffersLoaded
    ? seriesMessage.publishedUnknown
    : (templateRefused ??
      patternRefused ??
      (rows === null ? null : submissionRefusal({ rows, published: publishedHours })));

  const refusedRowIds = useMemo(() => {
    if (rows === null) return new Set<string>();
    return new Set([
      ...rowIdsEndingWhenTheyStart(rows),
      ...rowIdsClosingSaleBeforeItOpens(rows),
      ...rowIdsClosingSaleAfterItEnds(rows),
      ...rowIdsWithSaleAlreadyClosed(rows, new Date()),
      ...collidingRowIds(rows, publishedHours),
      ...overlappingRowIds(rows),
    ]);
  }, [rows, publishedHours]);

  function buildDrafts(): OfferDraft[] {
    if (rows === null || !template) return [];
    const { id, defaultPriceDiscounted, defaultCapacity } = template;
    if (defaultPriceDiscounted === null || defaultCapacity === null) return [];
    return rows.map((row) => ({
      activityId: id,
      startTime: row.startTime,
      endTime: row.endTime,
      capacity: defaultCapacity,
      priceDiscounted: defaultPriceDiscounted,
      saleWindow: { startsAt: row.saleOpensAt, endsAt: row.saleClosesAt },
      customFields: null,
    }));
  }

  return {
    form,
    template,
    rows,
    refusal,
    refusedRowIds,
    canDescribePattern,
    buildPreview,
    correctRow,
    removeRow,
    keepUnpublishedRows,
    reset,
    buildDrafts,
  };
}
